import React, { useEffect, useRef, useState } from 'react';
import api from '../axiosInstance';
import { useWorkflowSchema } from '../hooks/useWorkflowSchema';

// If GOV.UK Frontend JS is available globally, initialise after mount
function useGovUkInit() {
  useEffect(() => {
    if (window && window.GOVUKFrontend && typeof window.GOVUKFrontend.initAll === 'function') {
      try { window.GOVUKFrontend.initAll(); } catch (_) {}
    }
  }, []);
}

function t(lang, v) {
  if (v && typeof v === 'object') return v[lang] ?? v.en ?? v.fr ?? '';
  return v ?? '';
}

function resolveHtml(lang, comp) {
  if (!comp) return null;
  const raw = comp.html ?? comp.label ?? comp.text ?? null;
  if (!raw) return null;
  // Launch-mode behavior: allow authored raw HTML in dynamic intake and digital forms.
  if (typeof raw === 'string') return raw || null;
  if (typeof raw === 'object') return raw[lang] ?? raw.en ?? raw.fr ?? null;
  return null;
}

export function Radio({ comp, lang = 'en', value, onChange, error, render, values }) {
  useGovUkInit();
  // Allow explicit name override; fall back to storageKey then id
  const name = comp.name || comp.storageKey || comp.id || 'radio';
  // Accept both shapes: items[] (old) or options[] (published schema)
  const items = Array.isArray(comp.items)
    ? comp.items
    : (Array.isArray(comp.options) ? comp.options : []);
  // Allow explicit idPrefix override for stable per-item ids
  const idBase = comp.idPrefix || name;
  const hintId = `${idBase}-hint`;
  const errorId = `${idBase}-error`;
  const describedBy = [comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  // Normalization bridge: radios using normalize: 'yn-01' or 'yn-boolean' are stored as 1/0/true/false
  // while option values in authoring often remain 'yes'/'no'. We treat these as equivalent so
  // previously answered values hydrate correctly when navigating back.
  function canonicalYesNo(v) {
    if (v === undefined || v === null) return v;
    if (v === true) return 'yes';
    if (v === false) return 'no';
    if (v === 1 || v === '1') return 'yes';
    if (v === 0 || v === '0') return 'no';
    if (typeof v === 'string') {
      const s = v.toLowerCase();
      if (['yes','y','true'].includes(s)) return 'yes';
      if (['no','n','false'].includes(s)) return 'no';
    }
    return v;
  }
  function valuesEquivalent(a, b) {
    // Only apply special mapping if the component (or its schema) indicates yes/no normalization semantics
    const wantsMapping = ['yn-01','yn-boolean'].includes(String(comp.normalize||'').toLowerCase());
    if (!wantsMapping) return String(a) === String(b);
    return String(canonicalYesNo(a)) === String(canonicalYesNo(b));
  }
  return (
    <div className={`govuk-form-group${error ? ' govuk-form-group--error' : ''}`}>
      <fieldset className="govuk-fieldset" aria-describedby={describedBy}>
        {comp.label && (
          <legend className={`govuk-fieldset__legend${comp.legendClass ? ' ' + comp.legendClass : ' govuk-fieldset__legend--m'}`}>{t(lang, comp.label)}</legend>
        )}
        {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
        {error && (
          <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>
        )}
        {(() => {
          const base = 'govuk-radios';
          const extras = (comp.class || '')
            .split(/\s+/)
            .filter(c => c && c !== base)
            .join(' ');
          const containerClass = extras ? `${base} ${extras}` : base;
          return (
            <div className={containerClass} data-module="govuk-radios">
              {items.map((it, idx) => {
                const id = it.id || `${idBase}-${idx}`;
                const checked = valuesEquivalent(value, it.value);
                const reveal = Array.isArray(it.children) ? it.children : (it.conditional && Array.isArray(it.conditional.children) ? it.conditional.children : null);
                const revealId = reveal ? `${id}-reveal` : undefined;
                return (
                  <div className="govuk-radios__item" key={id}>
                    <input
                      className="govuk-radios__input"
                      id={id}
                      name={name}
                      type="radio"
                      value={String(it.value)}
                      checked={!!checked}
                      onChange={() => onChange && onChange(it.value)}
                      {...(reveal ? { 'data-aria-controls': revealId } : {})}
                    />
                    <label className="govuk-label govuk-radios__label" htmlFor={id}>
                      {t(lang, it.label)}
                    </label>
                    {it.hint && <div className="govuk-hint govuk-radios__hint">{t(lang, it.hint)}</div>}
                    {reveal && (
                      <div className="govuk-radios__conditional" id={revealId} hidden={!checked}>
                        {checked && reveal.map((child, i) => render ? render({ ...child, id: child.id || `${id}-child-${i}` }) : null)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
  })()}
      </fieldset>
    </div>
  );
}

export function Panel({ comp, lang = 'en' }) {
  useGovUkInit();
  // Accept both shapes: title/body (old) or label/hint (published schema)
  const title = comp.title ?? comp.label;
  const body = comp.body ?? comp.hint;
  return (
    <div className="govuk-panel govuk-panel--confirmation">
      {title && <h2 className="govuk-panel__title">{t(lang, title)}</h2>}
      {body && <div className="govuk-panel__body">{t(lang, body)}</div>}
    </div>
  );
}

export function Label({ comp, lang = 'en' }) {
  const level = comp.level || 0;
  const text = comp.text ?? comp.label;
  if (level >= 1 && level <= 3) {
    const cls = level === 1 ? 'govuk-heading-l' : level === 2 ? 'govuk-heading-m' : 'govuk-heading-s';
    return <h2 className={cls}>{t(lang, text)}</h2>;
  }
  return <p className="govuk-body">{t(lang, text)}</p>;
}

export function Paragraph({ comp, lang = 'en' }) {
  const cls = comp.class || 'govuk-body';
  const html = resolveHtml(lang, comp);
  const text = comp.text ?? comp.label ?? comp.hint ?? '';
  if (cls.includes('govuk-heading-xl')) return html ? <h1 className="govuk-heading-xl" dangerouslySetInnerHTML={{ __html: html }} /> : <h1 className="govuk-heading-xl">{t(lang, text)}</h1>;
  if (cls.includes('govuk-heading-l')) return html ? <h2 className="govuk-heading-l" dangerouslySetInnerHTML={{ __html: html }} /> : <h2 className="govuk-heading-l">{t(lang, text)}</h2>;
  if (cls.includes('govuk-heading-m')) return html ? <h3 className="govuk-heading-m" dangerouslySetInnerHTML={{ __html: html }} /> : <h3 className="govuk-heading-m">{t(lang, text)}</h3>;
  if (cls.includes('govuk-heading-s')) return html ? <h4 className="govuk-heading-s" dangerouslySetInnerHTML={{ __html: html }} /> : <h4 className="govuk-heading-s">{t(lang, text)}</h4>;
  if (cls.includes('govuk-inset-text')) return html ? <div className="govuk-inset-text" dangerouslySetInnerHTML={{ __html: html }} /> : <div className="govuk-inset-text">{t(lang, text)}</div>;
  if (html) return <div className={cls} dangerouslySetInnerHTML={{ __html: html }} />;
  return <p className={cls}>{t(lang, text)}</p>;
}

export function InsetText({ comp, lang = 'en' }) {
  const html = resolveHtml(lang, comp);
  if (html) return <div className="govuk-inset-text" dangerouslySetInnerHTML={{ __html: html }} />;
  const text = comp.text ?? comp.label ?? comp.hint;
  return <div className="govuk-inset-text">{t(lang, text)}</div>;
}

export function WarningText({ comp, lang = 'en' }) {
  const text = comp.text ?? comp.label ?? comp.hint;
  // Minimal assistive text translation
  const assistive = lang === 'fr' ? 'Avertissement' : 'Warning';
  return (
    <div className="govuk-warning-text">
      <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
      <strong className="govuk-warning-text__text">
        <span className="govuk-warning-text__assistive">{assistive}</span>
        {t(lang, text)}
      </strong>
    </div>
  );
}

export function Details({ comp, lang = 'en' }) {
  const summary = comp.summary ?? comp.label ?? { en: 'Details' };
  const body = comp.text ?? comp.hint ?? '';
  return (
    <details className="govuk-details" data-module="govuk-details">
      <summary className="govuk-details__summary">
        <span className="govuk-details__summary-text">{t(lang, summary)}</span>
      </summary>
      <div className="govuk-details__text">{t(lang, body)}</div>
    </details>
  );
}

export function Accordion({ comp, lang = 'en' }) {
  // Accept either comp.sections or comp.children: [{ heading, text }]
  const sections = Array.isArray(comp.sections) ? comp.sections : (Array.isArray(comp.children) ? comp.children : []);
  const accId = comp.id || comp.storageKey || 'accordion';
  return (
    <div className="govuk-accordion" data-module="govuk-accordion" id={accId}>
      {sections.map((s, idx) => {
        const secId = `${accId}-section-${idx}`;
        const heading = t(lang, s.heading || s.label || `Section ${idx + 1}`);
        const body = t(lang, s.text || s.hint || '');
        return (
          <div className="govuk-accordion__section" key={secId}>
            <div className="govuk-accordion__section-header">
              <h2 className="govuk-accordion__section-heading" id={`${secId}-heading`}>
                <button type="button" className="govuk-accordion__section-button" aria-controls={`${secId}-content`} aria-expanded="false">
                  {heading}
                </button>
              </h2>
            </div>
            <div id={`${secId}-content`} className="govuk-accordion__section-content" aria-labelledby={`${secId}-heading`}>
              <p className="govuk-body">{body}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CharacterCount({ comp, lang = 'en', value = '', onChange, error }) {
  useGovUkInit();
  const name = comp.storageKey || comp.id || 'character-count';
  const id = name;
  const hintId = `${id}-hint`;
  const messageId = `${id}-info`;
  const errorId = `${id}-error`;
  const max = comp.maxLength || (comp.validation && comp.validation.maxLength) || 200;
  const maxWords = comp.maxWords || (comp.validation && comp.validation.maxWords);
  const threshold = comp.threshold || (comp.validation && comp.validation.threshold); // percentage (e.g. 80)
  const textarea = comp.textarea !== false; // default to textarea
  const clsWrapper = `govuk-character-count`;
  const clsControlBase = textarea ? (comp.class || 'govuk-textarea') : (comp.class || 'govuk-input');
  const required = !!comp.required;

  const computeRemaining = (current) => {
    if (maxWords) {
      const words = (current || '').trim().split(/\s+/).filter(Boolean).length;
      return maxWords - words; // remaining words (negative if over)
    }
    return max - (current || '').length; // remaining chars (negative if over)
  };

  const remainingMessage = (current) => {
    if (maxWords) {
      const words = (current || '').trim().split(/\s+/).filter(Boolean).length;
      const remaining = maxWords - words;
      if (remaining <= 0) return lang === 'fr' ? `Vous avez dépassé la limite de ${maxWords} mots` : `You have exceeded the limit of ${maxWords} words`;
      return lang === 'fr'
        ? `Il vous reste ${remaining} mot${remaining === 1 ? '' : 's'}`
        : `${remaining} word${remaining === 1 ? '' : 's'} remaining`;
    }
    const chars = (current || '').length;
    const remaining = max - chars;
    if (remaining < 0) {
      const over = Math.abs(remaining);
      return lang === 'fr'
        ? `Vous avez dépassé la limite de ${max} caractères de ${over}`
        : `${over} character${over === 1 ? '' : 's'} too many`;
    }
    return lang === 'fr'
      ? `Il vous reste ${remaining} caractère${remaining === 1 ? '' : 's'}`
      : `${remaining} character${remaining === 1 ? '' : 's'} remaining`;
  };

  const getDynamicClasses = (current) => {
    const remaining = computeRemaining(current);
    const nearLimit = threshold && max && !maxWords && remaining <= Math.ceil(max * (1 - threshold / 100)) && remaining >= 0; // inside warning band
    const over = remaining < 0;
    return { remaining, nearLimit, over };
  };

  const initialState = getDynamicClasses(value);
  const [counter, setCounter] = useState(() => remainingMessage(value));
  const [state, setState] = useState(initialState); // { remaining, nearLimit, over }

  const updateState = (nextVal) => {
    setCounter(remainingMessage(nextVal));
    setState(getDynamicClasses(nextVal));
  };

  useEffect(() => {
    updateState(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, max, maxWords, threshold]);

  const handleChange = (e) => {
    onChange && onChange(e.target.value);
    updateState(e.target.value);
  };

  const ds = [];
  if (max && !maxWords) ds.push(['data-maxlength', String(max)]);
  if (maxWords) ds.push(['data-maxwords', String(maxWords)]);
  if (threshold) ds.push(['data-threshold', String(threshold)]);
  const dsProps = Object.fromEntries(ds);
  const describedBy = [comp.hint ? hintId : null, error ? errorId : null, messageId].filter(Boolean).join(' ');

  const controlDynamicClass = state.over
    ? (textarea ? 'govuk-textarea--error' : 'govuk-input--error')
    : state.nearLimit
      ? (textarea ? 'govuk-textarea--near-limit' : 'govuk-input--near-limit')
      : '';
  const clsControl = `${clsControlBase}${controlDynamicClass ? ' ' + controlDynamicClass : ''}`;

  const effectiveError = state.over ? (lang === 'fr' ? 'Trop de caractères' : 'Too many characters') : error;
  const finalError = effectiveError || (required ? null : null);
  return (
    <div className={clsWrapper} data-module="govuk-character-count" {...dsProps}>
      <div className={`govuk-form-group${finalError ? ' govuk-form-group--error' : ''}`}>
        {comp.label && (
          <label className={`govuk-label${comp.labelClass ? ' ' + comp.labelClass : ''}`} htmlFor={id}>{t(lang, comp.label)}</label>
        )}
        {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
        {finalError && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {finalError}</p>}
        {textarea ? (
          <textarea
            className={clsControl}
            id={id}
            name={name}
            rows={comp.rows || 5}
            aria-describedby={describedBy}
            value={value || ''}
            onChange={handleChange}
            required={required}
            maxLength={!maxWords && max ? max : undefined}
          />
        ) : (
          <input
            className={clsControl}
            id={id}
            name={name}
            type="text"
            aria-describedby={describedBy}
            value={value || ''}
            onChange={handleChange}
            required={required}
            maxLength={!maxWords && max ? max : undefined}
          />
        )}
      </div>
      <div id={messageId} className={`govuk-hint govuk-character-count__message${state.over ? ' govuk-character-count__message--warning' : state.nearLimit ? ' govuk-character-count__message--near-limit' : ''}`} aria-live="polite">
        {counter}
      </div>
    </div>
  );
}

export function FileUpload({ comp, lang = 'en', value, onChange, error }) {
  useGovUkInit();
  const FILE_UPLOAD_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.heic', '.webp']);
  const normaliseAccept = (acceptValue) => {
    const acceptForPolicy = acceptValue || '.jpg,.jpeg,.png,.gif,.heic';
    const parsedAccept = acceptForPolicy.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    const imageOnly = parsedAccept.length === 0
      ? true
      : parsedAccept.every(item => item.startsWith('image/') || FILE_UPLOAD_IMAGE_EXTENSIONS.has(item));
    const acceptList = imageOnly
      ? Array.from(new Set(['image/*', ...parsedAccept]))
      : parsedAccept;
    return {
      acceptForPolicy,
      imageOnly,
      accept: acceptList.join(','),
    };
  };
  const supportsCameraCaptureChoice = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || typeof document === 'undefined') {
      return false;
    }
    const userAgent = navigator.userAgent || '';
    const mobileUserAgent = /android|iphone|ipad|ipod|mobile|silk/i.test(userAgent);
    const hasTouch = Number(navigator.maxTouchPoints || 0) > 0;
    const coarsePointer = typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
    const input = document.createElement('input');
    return 'capture' in input && (mobileUserAgent || (hasTouch && coarsePointer));
  };
  const pickerRef = useRef(null);
  const cameraInputRef = useRef(null);
  const chooserRef = useRef(null);
  const name = comp.storageKey || comp.id || 'file';
  const id = name;
  const applicationId =
    comp.applicationId ||
    comp.application_id ||
    comp?.props?.applicationId ||
    comp?.props?.application_id ||
    null;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const multiple = !!comp.multiple;
  const { acceptForPolicy, imageOnly, accept } = normaliseAccept(comp.accept);
  const cls = comp.class || 'govuk-file-upload';
  const required = !!comp.required;
  const maxSizeMb = comp.maxSizeMb || comp.maxSize || null;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [cameraChooserAvailable, setCameraChooserAvailable] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);

  const FILE_UPLOAD_VALUE_KEYS = ['filePath', 'file_path', 'path', 'key', 'fileId', 'file_id', 'name', 'originalFilename', 'original_filename'];
  const isValidUploadEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return false;
    return FILE_UPLOAD_VALUE_KEYS.some((k) => {
      const v = entry[k];
      if (v === undefined || v === null) return false;
      if (typeof v === 'string') return v.trim() !== '';
      if (typeof v === 'number') return Number.isFinite(v);
      return false;
    });
  };
  const hasInvalidUploadEntries = (input) => {
    if (!input) return false;
    const entries = Array.isArray(input) ? input : [input];
    return entries.some(entry => !isValidUploadEntry(entry));
  };
  const normalizeUploadValue = (input) => {
    if (!input) return multiple ? [] : null;
    const entries = Array.isArray(input) ? input : [input];
    const cleaned = entries.filter(isValidUploadEntry);
    if (multiple) return cleaned;
    return cleaned[0] || null;
  };

  const [uploaded, setUploaded] = useState(() => normalizeUploadValue(value));
  const hasUploadedFiles = Array.isArray(uploaded) ? uploaded.length > 0 : !!uploaded;

  const documentType = comp.documentType || comp.document_type || (comp.metadata && comp.metadata.document_type) || '';
  const documentLabel =
    (typeof comp.documentLabel === 'object' ? (comp.documentLabel[lang] || comp.documentLabel.en) : comp.documentLabel) ||
    comp.document_label ||
    (comp.metadata && comp.metadata.document_label) ||
    '';

  useEffect(() => () => {
    previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  useEffect(() => {
    setCameraChooserAvailable(supportsCameraCaptureChoice());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chooserOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!chooserRef.current || chooserRef.current.contains(event.target)) return;
      setChooserOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setChooserOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [chooserOpen]);

  useEffect(() => {
    const normalized = normalizeUploadValue(value);
    const invalid = hasInvalidUploadEntries(value);
    const currentSerialized = JSON.stringify(uploaded ?? null);
    const nextSerialized = JSON.stringify(normalized);
    if (invalid) {
      setUploaded(normalized);
      if (onChange) onChange(normalized);
      return;
    }
    if (nextSerialized !== currentSerialized) {
      setUploaded(normalized);
    }
  }, [value, multiple, onChange, uploaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFilePicker = () => {
    setChooserOpen(false);
    if (pickerRef.current) pickerRef.current.click();
  };

  const openCameraPicker = () => {
    setChooserOpen(false);
    if (cameraInputRef.current) cameraInputRef.current.click();
  };

  const handlePrimaryAction = () => {
    if (!cameraChooserAvailable) {
      openFilePicker();
      return;
    }
    setChooserOpen(prev => !prev);
  };

  const validateFile = (file) => {
    if (imageOnly && !(file?.type || '').startsWith('image/')) {
      return lang === 'fr' ? 'Images uniquement.' : 'Images only.';
    }
    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
      return (lang === 'fr' ? 'Taille maximale ' : 'Maximum size ') + `${maxSizeMb}MB`;
    }
    return null;
  };

  async function legacyDirectUpload(file, options = {}) {
    const form = new FormData();
    form.append('file', file);
    if (documentType) form.append('document_type', documentType);
    if (options.uploadId) form.append('uploadId', options.uploadId);
    const res = await api.post('/api/upload-application-file', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return { fileId: res.data?.fileId, filePath: res.data?.filePath, name: file.name, size: file.size, type: file.type };
  }

  async function presignAndUpload(file, labelOverride) {
    const effectiveLabel = labelOverride || documentLabel || '';
    const presignPayload = {
      filename: file.name,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      document_type: documentType || '',
      document_label: effectiveLabel,
      accept: acceptForPolicy,
      maxSizeMb: maxSizeMb || undefined,
      application_id: applicationId || undefined,
    };
    let presign;
    try {
      presign = await api.post('/api/uploads/presign', presignPayload).then(r => r.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        return legacyDirectUpload(file);
      }
      throw e;
    }
    if (presign.mode === 'local-direct') {
      const result = await legacyDirectUpload(file, { uploadId: presign.uploadId });
      await api.post('/api/documents/finalize', {
        uploadId: presign.uploadId,
        filePath: result.filePath,
        originalFilename: file.name,
        document_type: documentType || '',
        document_label: effectiveLabel,
        application_id: applicationId || undefined,
      });
      return result;
    } else if (presign.mode === 's3') {
      const s3BaseMessage = lang === 'fr' ? 'Echec du televersement S3' : 'S3 upload failed';
      const endpointHint = presign?.presigned?.url?.includes('localhost:9000')
        ? (lang === 'fr'
          ? 'Assurez-vous que MinIO est demarre sur http://localhost:9000 (executer "npm run MinIO").'
          : 'Make sure MinIO is running at http://localhost:9000 (run "npm run MinIO").')
        : (lang === 'fr'
          ? "Verifier que le service de stockage d'objets est accessible."
          : 'Check that the object storage service is reachable.');
      let putResponse;
      try {
        putResponse = await fetch(presign.presigned.url, {
          method: presign.presigned.method || 'PUT',
          headers: presign.presigned.headers || { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
      } catch (putErr) {
        const underlying = putErr?.message || '';
        const networkDetail = underlying && underlying !== 'Failed to fetch'
          ? underlying
          : (lang === 'fr' ? 'Impossible de contacter le service de stockage.' : 'Could not reach the object storage service.');
        const message = (s3BaseMessage + ': ' + networkDetail + ' ' + endpointHint).trim();
        throw new Error(message);
      }
      if (!putResponse.ok) {
        const statusInfo = [putResponse.status, putResponse.statusText].filter(Boolean).join(' ').trim();
        let bodyText = '';
        try {
          bodyText = await putResponse.text();
        } catch (_) {
          bodyText = '';
        }
        const responseDetail = [statusInfo || null, bodyText ? bodyText.slice(0, 200) : null].filter(Boolean).join(' - ');
        const detail = responseDetail || (lang === 'fr' ? 'Reponse inattendue du service.' : 'Unexpected response from object storage.');
        const message = (s3BaseMessage + ': ' + detail + ' ' + endpointHint).trim();
        throw new Error(message);
      }

      await api.post('/api/documents/finalize', {
        uploadId: presign.uploadId,
        key: presign.key,
        originalFilename: file.name,
        document_type: documentType || '',
        document_label: effectiveLabel,
        application_id: applicationId || undefined,
      });
      return { fileId: null, filePath: presign.key, name: file.name, size: file.size, type: file.type };
    }
    throw new Error('Unsupported upload mode: ' + presign.mode);
  }

  const uploadOne = async (file, labelOverride) => presignAndUpload(file, labelOverride);

  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setChooserOpen(false);
    const validationError = files.map(validateFile).find(Boolean);
    if (validationError) {
      setUploadError(validationError);
      e.target.value = '';
      return;
    }
    setUploadError(null);
    const imagePreviews = files.filter(f => (f.type || '').startsWith('image/')).map(f => ({ url: URL.createObjectURL(f), name: f.name }));
    setPreviews(prev => multiple ? [...prev, ...imagePreviews] : imagePreviews);
    setUploading(true);
    try {
      if (multiple) {
        const existing = normalizeUploadValue(uploaded);
        const results = [];
        const total = files.length;
        for (let idx = 0; idx < files.length; idx += 1) {
          const f = files[idx];
          const labelForFile = documentLabel
            ? `${documentLabel}${total > 1 ? ` (${idx + 1})` : ''}`
            : '';
          // eslint-disable-next-line no-await-in-loop
          const r = await uploadOne(f, labelForFile);
          results.push(r);
        }
        const next = [...existing, ...results];
        setUploaded(next);
        onChange && onChange(next);
      } else {
        const r = await uploadOne(files[0], documentLabel || '');
        setUploaded(r);
        onChange && onChange(r);
      }
      e.target.value = '';
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || (lang === 'fr' ? 'Échec du téléversement.' : 'Upload failed.');
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const removeOne = async (fileObj, index) => {
    const filePath = fileObj?.filePath || fileObj?.path;
    if (!filePath) return;
    const clearRemovedFile = () => {
      if (Array.isArray(uploaded)) {
        const next = uploaded.filter((_, i) => i !== index);
        setUploaded(next);
        setPreviews(prev => prev.filter((_, i) => i !== index));
        onChange && onChange(next);
      } else {
        setUploaded(null);
        setPreviews([]);
        onChange && onChange(null);
      }
    };
    setUploadError(null);
    setRemoving(true);
    try {
      const response = await api.delete('/api/uploads/remove', { data: { filePath } });
      clearRemovedFile();
      if (response?.data?.storageCleanupPending === true) {
        setUploadError((lang === 'fr' ? response.data.message_fr : response.data.message) || (lang === 'fr'
          ? 'Le fichier a été retiré, mais le nettoyage du stockage doit être terminé par le soutien.'
          : 'File removed, but support needs to finish storage cleanup.'));
      }
    } catch (err) {
      if (err?.response?.data?.removed === true) clearRemovedFile();
      const msg = err?.response?.data?.message || err?.message || (lang === 'fr' ? 'Échec de la suppression.' : 'Remove failed.');
      setUploadError(msg);
    } finally {
      setRemoving(false);
    }
  };

  const buttonLabel = lang === 'fr' ? 'Téléverser' : 'Upload';
  const chooserHint = lang === 'fr'
    ? 'Sur un téléphone compatible, choisissez de prendre une photo ou de sélectionner un fichier.'
    : 'On a supported phone, choose whether to take a photo or select a file.';
  const takePhotoLabel = lang === 'fr' ? 'Prendre une photo' : 'Take photo';
  const chooseFileLabel = lang === 'fr' ? 'Choisir un fichier' : 'Choose file';
  return (
    <div className={`govuk-form-group${error ? ' govuk-form-group--error' : ''}`}>
      {comp.label && <label className={`govuk-label${comp.labelClass ? ' ' + comp.labelClass : ''}`} htmlFor={id}>{t(lang, comp.label)}</label>}
      {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
      {error && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>}

      <input
        ref={pickerRef}
        className={cls}
        id={`${id}-picker`}
        name={name}
        type="file"
        accept={accept}
        aria-describedby={[comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}
        multiple={multiple}
        required={required && !hasUploadedFiles}
        onChange={handleChange}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
      <input
        ref={cameraInputRef}
        className={cls}
        id={`${id}-camera`}
        name={`${name}-camera`}
        type="file"
        accept="image/*"
        capture="environment"
        aria-describedby={[comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}
        multiple={false}
        required={false}
        onChange={handleChange}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
      <div className="govuk-button-group">
        <button
          type="button"
          onClick={handlePrimaryAction}
          className={`govuk-button${hasUploadedFiles ? ' govuk-button--secondary' : ''}`}
          data-module="govuk-button"
          disabled={uploading}
          aria-expanded={cameraChooserAvailable ? chooserOpen : undefined}
          aria-controls={cameraChooserAvailable ? `${id}-chooser` : undefined}
        >
          {buttonLabel}
        </button>
      </div>
      {cameraChooserAvailable && chooserOpen && (
        <div
          id={`${id}-chooser`}
          ref={chooserRef}
          className="file-upload__chooser"
          role="group"
          aria-label={lang === 'fr' ? 'Options de téléversement' : 'Upload options'}
        >
          <div className="file-upload__chooser-copy">{chooserHint}</div>
          <div className="file-upload__chooser-actions">
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              data-module="govuk-button"
              onClick={openCameraPicker}
              disabled={uploading}
            >
              {takePhotoLabel}
            </button>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              data-module="govuk-button"
              onClick={openFilePicker}
              disabled={uploading}
            >
              {chooseFileLabel}
            </button>
          </div>
        </div>
      )}

      {comp.showMimeList && accept && (
        <div className="govuk-hint govuk-!-margin-top-1">
          {lang === 'fr' ? 'Types acceptés:' : 'Accepted types:'} {accept}
        </div>
      )}
      {comp.showMaxSize && maxSizeMb && (
        <div className="govuk-hint govuk-!-margin-top-1">
          {lang === 'fr' ? 'Taille maximale:' : 'Maximum size:'} {maxSizeMb} MB
        </div>
      )}
      {previews.length > 0 && (
        <div className="govuk-hint govuk-!-margin-top-2" aria-live="polite">
          {lang === 'fr' ? 'Aperçu:' : 'Preview:'}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            {previews.map((p, i) => (
              <div key={`${p.url}-${i}`} style={{ width: 96, height: 96, borderRadius: 4, border: '1px solid #d0d0d0', background: '#f8f8f8', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={p.url} alt={p.name || 'preview'} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            ))}
          </div>
        </div>
      )}
      {uploading && (
        <div className="govuk-hint" aria-live="polite">{lang === 'fr' ? 'Téléversement en cours…' : 'Uploading…'}</div>
      )}
      {uploadError && (
        <p className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {uploadError}</p>
      )}
      {uploaded && (
        Array.isArray(uploaded) ? (
          uploaded.length > 0 && (
            <ul className="govuk-list govuk-!-margin-top-2">
              {uploaded.map((u, i) => {
                const sizeKB = u.size ? ` · ${(u.size/1024).toFixed(1)} KB` : '';
                const type = u.type ? ` · ${u.type}` : '';
                return (
                <li key={i}>
                  <span>{u.name || u.original_filename || (lang === 'fr' ? 'Fichier' : 'File')} {u.fileId ? `(ID ${u.fileId})` : ''}{sizeKB}{type}</span>
                  {' '}
                  <button
                    type="button"
                    className="govuk-button govuk-button--secondary file-upload__remove-button"
                    aria-label={(lang === 'fr' ? 'Supprimer' : 'Remove') + ' ' + (u.name || u.original_filename || '')}
                    onClick={() => removeOne(u, i)}
                    disabled={removing || uploading}
                  >
                    {removing ? (lang === 'fr' ? 'Suppression…' : 'Removing…') : (lang === 'fr' ? 'Supprimer' : 'Remove')}
                  </button>
                </li>
              );})}
            </ul>
          )
        ) : (
          <div className="govuk-hint govuk-!-margin-top-2">
            {((uploaded.name || uploaded.original_filename)) ? (() => { const sizeKB = uploaded.size ? ` · ${(uploaded.size/1024).toFixed(1)} KB` : ''; const type = uploaded.type ? ` · ${uploaded.type}` : ''; return (
              <>
                <span>{(lang === 'fr' ? 'Téléversé:' : 'Uploaded:') + ' ' + (uploaded.name || uploaded.original_filename)}{sizeKB}{type}</span>
                {' '}
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary file-upload__remove-button"
                  aria-label={(lang === 'fr' ? 'Supprimer' : 'Remove') + ' ' + (uploaded.name || uploaded.original_filename || '')}
                  onClick={() => removeOne(uploaded, 0)}
                  disabled={removing || uploading}
                >
                  {removing ? (lang === 'fr' ? 'Suppression…' : 'Removing…') : (lang === 'fr' ? 'Supprimer' : 'Remove')}
                </button>
              </>
            ); })() : null}
          </div>
        )
      )}
    </div>
  );
}

export function SignatureAck({ comp, lang = 'en', value, onChange, error }) {
  useGovUkInit();
  const name = comp.storageKey || comp.id || 'signature_ack';
  const id = name;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const required = !!comp.required;
  const actionLabel = t(lang, comp.actionLabel) || (lang === 'fr' ? 'Signer maintenant' : 'Sign Now');
  const clearLabel = t(lang, comp.clearLabel) || (lang === 'fr' ? 'Effacer' : 'Clear');
  const placeholder = t(lang, comp.placeholder) || (lang === 'fr' ? 'Tapez votre nom complet' : 'Type your full name');
  const statusSignedText = t(lang, comp.statusSignedText) || (lang === 'fr' ? 'Sign\u00e9' : 'Signed');
  const statusUnsignedText = t(lang, comp.statusUnsignedText) || (lang === 'fr' ? 'Non sign\u00e9' : 'Not signed');
  const paddingKey = String(comp.boxPadding || 'm').toLowerCase();
  const supportedPadding = ['s', 'm', 'l', 'xl'].includes(paddingKey) ? paddingKey : 'm';
  const handwritingFont = comp.handwritingFont || 'cursive';
  const formGroupClass = comp.formGroupClass ? ` ${comp.formGroupClass}` : '';
  const valueObj = value && typeof value === 'object' ? value : null;
  const signed = !!(valueObj && valueObj.signed);
  const signedName = valueObj?.name || '';
  const [localName, setLocalName] = useState(() => {
    if (signed) return signedName;
    if (typeof value === 'string') return value;
    return '';
  });

  useEffect(() => {
    if (value && typeof value === 'object') {
      setLocalName(value.name || '');
    } else if (typeof value === 'string') {
      setLocalName(value);
    } else {
      setLocalName('');
    }
  }, [value]);

  const canSign = !signed && localName.trim().length > 0;
  const effectiveError = error || null;
  const containerClasses = ['signature-ack__container', `signature-ack__container--${supportedPadding}`, signed ? 'signature-ack__container--signed' : ''].filter(Boolean).join(' ');

  const handleSign = () => {
    if (!canSign) return;
    onChange && onChange({ signed: true, name: localName.trim() });
  };

  const handleClear = () => {
    setLocalName('');
    onChange && onChange(undefined);
  };

  return (
    <div className={`govuk-form-group${effectiveError ? ' govuk-form-group--error' : ''}${formGroupClass}`}>
      {comp.label && (
        <label className={`govuk-label${comp.labelClass ? ' ' + comp.labelClass : ''}`} htmlFor={id}>{t(lang, comp.label)}</label>
      )}
      {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
      {effectiveError && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {effectiveError}</p>}
      <div className={containerClasses}>
        <div className="signature-ack__input-wrap">
          <input
            id={id}
            name={name}
            type="text"
            className={`govuk-input signature-ack__input${signed ? ' signature-ack__input--locked sig-locked' : ''}`}
            style={signed ? { fontFamily: handwritingFont } : undefined}
            value={signed ? signedName : localName}
            onChange={e => { if (!signed) setLocalName(e.target.value); }}
            placeholder={placeholder}
            readOnly={signed}
            aria-describedby={[comp.hint ? hintId : null, effectiveError ? errorId : null].filter(Boolean).join(' ') || undefined}
            aria-invalid={effectiveError ? 'true' : undefined}
            required={required}
          />
        </div>
        <div className="signature-ack__buttons">
          {!signed && (
            <button
              type="button"
              className="govuk-button signature-ack__button"
              data-module="govuk-button"
              onClick={handleSign}
              disabled={!canSign}
            >
              {actionLabel}
            </button>
          )}
          {signed && (
            <button
              type="button"
              className="govuk-button govuk-button--secondary signature-ack__button"
              data-module="govuk-button"
              onClick={handleClear}
            >
              {clearLabel}
            </button>
          )}
        </div>
        <div className="govuk-hint signature-ack__status" aria-live="polite">
          {signed ? statusSignedText : statusUnsignedText}
        </div>
      </div>
    </div>
  );
}

// (Old Input removed; enhanced Input defined later in file)

export function Textarea({ comp, lang = 'en', value = '', onChange, error }) {
  useGovUkInit();
  const name = comp.storageKey || comp.id || 'textarea';
  const id = name;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const clsBase = comp.class || 'govuk-textarea';
  const cls = error ? `${clsBase} govuk-textarea--error` : clsBase;
  const required = !!comp.required;
  const rows = comp.rows || 5;
  const handleChange = (e) => onChange && onChange(e.target.value);
  const handleBlur = (e) => {
    if (typeof comp.normalize === 'string' && comp.normalize.toLowerCase() === 'trim') {
      const trimmed = (e.target.value || '').trim();
      if (trimmed !== e.target.value) onChange && onChange(trimmed);
    }
  };
  return (
    <div className={`govuk-form-group${error ? ' govuk-form-group--error' : ''}`}>
      {comp.label && (
        <label className={`govuk-label${comp.labelClass ? ' ' + comp.labelClass : ''}`} htmlFor={id}>
          {t(lang, comp.label)}
        </label>
      )}
      {comp.hint && (
        <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>
      )}
      {error && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>}
      <textarea
        className={cls}
        id={id}
        name={name}
        rows={rows}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-describedby={[comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}
        required={required}
            aria-invalid={error ? 'true' : undefined}
      />
    </div>
  );
}

export function Select({ comp, lang = 'en', value, onChange, error }) {
  useGovUkInit();
  const name = comp.storageKey || comp.id || 'select';
  const id = name;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const clsBase = comp.class || 'govuk-select';
  const cls = error ? `${clsBase} govuk-select--error` : clsBase;
  const required = !!comp.required;
  const items = Array.isArray(comp.options) ? comp.options : [];
  const multiple = !!comp.multiple;
  const current = value === undefined || value === null ? (multiple ? [] : '') : value;
  const handleChange = (e) => {
    if (!onChange) return;
    if (multiple) {
      const selected = Array.from(e.target.selectedOptions).map(o => o.value);
      onChange(selected);
    } else {
      onChange(e.target.value);
    }
  };
  return (
    <div className={`govuk-form-group${error ? ' govuk-form-group--error' : ''}`}>
      {comp.label && (
        <label className={`govuk-label${comp.labelClass ? ' ' + comp.labelClass : ''}`} htmlFor={id}>{t(lang, comp.label)}</label>
      )}
      {comp.hint && (
        <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>
      )}
      {error && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>}
      <select
        className={cls}
        id={id}
        name={name}
        aria-describedby={[comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}
        required={required}
        multiple={multiple}
        value={current}
        onChange={handleChange}
        aria-invalid={error ? 'true' : undefined}
      >
        {!multiple && <option value="">{''}</option>}
        {items.map((it, idx) => (
          <option key={`${id}-${idx}`} value={String(it.value)}>{t(lang, it.label)}</option>
        ))}
      </select>
    </div>
  );
}

export function Checkbox({ comp, lang = 'en', value, onChange, error, render, values }) {
  useGovUkInit();
  // Allow explicit name override; fall back to storageKey then id
  const name = comp.name || comp.storageKey || comp.id || 'checkbox';
  const items = Array.isArray(comp.options) ? comp.options : [];
  const current = Array.isArray(value) ? value.map(String) : [];
  // Allow explicit idPrefix override for stable per-item ids
  const idBase = comp.idPrefix || name;
  const hintId = `${idBase}-hint`;
  const errorId = `${idBase}-error`;
  const describedBy = [comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const toggle = (val) => {
    if (!onChange) return;
    const sVal = String(val);
    const set = new Set(current);
    if (set.has(sVal)) set.delete(sVal); else set.add(sVal);
    onChange(Array.from(set));
  };
  return (
    <div className={`govuk-form-group${error ? ' govuk-form-group--error' : ''}`}>
      <fieldset className="govuk-fieldset" aria-describedby={describedBy}>
        {comp.label && (
          <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">{t(lang, comp.label)}</legend>
        )}
        {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
        {error && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>}
        {(() => {
          const base = 'govuk-checkboxes';
          const extras = (comp.class || '')
            .split(/\s+/)
            .filter(c => c && c !== base)
            .join(' ');
          const containerClass = extras ? `${base} ${extras}` : base;
          return (
            <div className={containerClass} data-module="govuk-checkboxes">
              {items.map((it, idx) => {
                const id = it.id || `${idBase}-${idx}`;
                const checked = current.includes(String(it.value));
                const reveal = Array.isArray(it.children) ? it.children : (it.conditional && Array.isArray(it.conditional.children) ? it.conditional.children : null);
                const revealId = reveal ? `${id}-reveal` : undefined;
                return (
                  <div className="govuk-checkboxes__item" key={id}>
                    <input
                      className="govuk-checkboxes__input"
                      id={id}
                      name={name}
                      type="checkbox"
                      value={String(it.value)}
                      checked={!!checked}
                      onChange={() => toggle(it.value)}
                      {...(reveal ? { 'data-aria-controls': revealId } : {})}
                    />
                    <label className="govuk-label govuk-checkboxes__label" htmlFor={id}>
                      {t(lang, it.label)}
                    </label>
                    {it.hint && <div className="govuk-hint govuk-checkboxes__hint">{t(lang, it.hint)}</div>}
                    {reveal && (
                      <div className="govuk-checkboxes__conditional" id={revealId} hidden={!checked}>
                        {checked && reveal.map((child, i) => render ? render({ ...child, id: child.id || `${id}-child-${i}` }) : null)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
  })()}
      </fieldset>
    </div>
  );
}
export function SummaryList({ comp, lang = 'en', values, onEdit }) {
  // New published structure: comp.rows[] each { key (source storageKey), label {en,fr}, format }
  const rows = Array.isArray(comp.rows) ? comp.rows : [];
  const hideEmpty = comp.hideEmpty !== false; // default true
  const { schema } = useWorkflowSchema();
  const intakeFormSchema = React.useMemo(() => (Array.isArray(schema) ? schema : []), [schema]);
  // Build (and cache) storageKey/id -> stepId index for inference when row.stepId missing
  const stepIndex = React.useMemo(() => {
    const map = {};
    if (Array.isArray(intakeFormSchema)) {
      for (const step of intakeFormSchema) {
        const sid = step.stepId;
        const comps = step.components || [];
        const stack = [...comps];
        while (stack.length) {
          const c = stack.pop();
            if (!c || typeof c !== 'object') continue;
            const k = c.storageKey || c.id;
            if (k && !map[k]) map[k] = sid;
            if (Array.isArray(c.options)) {
              for (const opt of c.options) {
                if (opt && Array.isArray(opt.children)) stack.push(...opt.children);
              }
            }
        }
      }
    }
    return map;
  }, [intakeFormSchema]);

  // Build option label index: key -> { multiple:boolean, labels: { value: {en,fr}|string } }
  const optionLabelIndex = React.useMemo(() => {
    const out = {};
    if (!Array.isArray(intakeFormSchema)) return out;
    const pushComp = (c) => {
      if (!c || typeof c !== 'object') return;
      const key = c.storageKey || c.id;
      if (!key) return;
      if (Array.isArray(c.options) && c.options.length) {
        const labels = {};
        for (const opt of c.options) {
          if (!opt || opt.value === undefined) continue;
          // Accept plain string or localized object label; store raw; decode later
          labels[String(opt.value)] = opt.label || opt.value;
          // Recurse into children to capture nested option components as separate components
          if (Array.isArray(opt.children)) opt.children.forEach(ch => pushComp(ch));
        }
        out[key] = { multiple: String(c.type).includes('checkbox'), labels };
      }
    };
    for (const step of intakeFormSchema) {
      const stack = [...(step.components || [])];
      while (stack.length) {
        const c = stack.pop();
        pushComp(c);
        if (c && Array.isArray(c.options)) {
          for (const opt of c.options) {
            if (opt && Array.isArray(opt.children)) stack.push(...opt.children);
          }
        }
      }
    }
    return out;
  }, [intakeFormSchema]);

  const yesNo = (val) => {
    if (val === true || val === 1 || String(val).toLowerCase() === 'true' || String(val).toLowerCase() === 'yes') return lang === 'fr' ? 'Oui' : 'Yes';
    if (val === false || val === 0 || String(val).toLowerCase() === 'false' || String(val).toLowerCase() === 'no') return lang === 'fr' ? 'Non' : 'No';
    if (val == null || val === '') return '';
    return String(val);
  };
  const formatCurrency = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return val == null ? '' : String(val);
    try { return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', { style: 'currency', currency: 'CAD' }).format(num); } catch (_) { return `$${num.toFixed(2)}`; }
  };
  const formatDate = (val) => {
    if (!val) return '';
    let y, m, d;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      [y, m, d] = val.split('-');
    } else if (typeof val === 'object') {
      y = val.year || val.y; m = val.month || val.m; d = val.day || val.d;
    }
    if (y && m && d) {
      const dt = new Date(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(dt.getTime())) {
        try { return dt.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'long', day: 'numeric' }); } catch (_) { return `${y}-${m}-${d}`; }
      }
    }
    return typeof val === 'string' ? val : JSON.stringify(val);
  };
  const asText = (val) => {
    if (val == null) return '';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };
  const applyFormat = (fmt, val) => {
    switch (fmt) {
      case 'yesno': return yesNo(val);
      case 'currency': return formatCurrency(val);
      case 'date': return formatDate(val);
      case 'text': default: return asText(val);
    }
  };

  const decodeValue = (candidateKeys, raw) => {
    if (raw == null || raw === '') return raw;
    const tryKeys = Array.isArray(candidateKeys) ? candidateKeys : [candidateKeys];
    for (const key of tryKeys.filter(Boolean)) {
      const meta = optionLabelIndex[key];
      if (!meta) continue;
      const mapLabels = (v) => {
        // exact match first
        let lbl = meta.labels[String(v)];
        // fallback: case-insensitive match
        if (!lbl) {
          const lower = String(v).toLowerCase();
            lbl = Object.entries(meta.labels).find(([val]) => val.toLowerCase() === lower)?.[1];
        }
        // yes/no canonicalization (handles stored 1/0/true/false for options defined as yes/no)
        if (!lbl) {
          const canon = (val) => {
            const s = String(val).toLowerCase();
            if (['1','yes','y','true'].includes(s)) return 'yes';
            if (['0','no','n','false'].includes(s)) return 'no';
            return null;
          };
          const c = canon(v);
          if (c && meta.labels[c]) lbl = meta.labels[c];
        }
        if (!lbl) return v; // fallback raw if unknown
        if (typeof lbl === 'object') return t(lang, lbl);
        return String(lbl);
      };
      if (Array.isArray(raw)) return raw.map(mapLabels);
      return mapLabels(raw);
    }
    return raw;
  };

  const rendered = rows.map(r => {
    // Support historical mistaken key -> originalKey; prefer primary key for raw lookup; use both for decoding
    const rawVal = values ? (values[r.key] !== undefined ? values[r.key] : (r.originalKey ? values[r.originalKey] : undefined)) : undefined;
    const decodeKeys = r.key === r.originalKey ? [r.key] : [r.key, r.originalKey];
    const decoded = decodeValue(decodeKeys, rawVal);
    const formatted = applyFormat(r.format || 'text', decoded);
    const inferredStep = r.stepId || stepIndex[r.key] || (r.originalKey ? stepIndex[r.originalKey] : undefined);
    return { label: t(lang, r.label || r.key), value: formatted, stepId: inferredStep };
  });

  const filtered = hideEmpty ? rendered.filter(r => r.value !== undefined && r.value !== null && String(r.value).trim() !== '') : rendered;
  if (filtered.length === 0) {
    if (comp.emptyFallback) {
      return <p className="govuk-body">{t(lang, comp.emptyFallback)}</p>;
    }
    return null;
  }
  return (
    <dl className="govuk-summary-list">
      {filtered.map((r, i) => {
        const actionText = comp.actionLabel ? t(lang, comp.actionLabel) : (lang === 'fr' ? 'Modifier' : 'Modify');
        const basePath = comp.editBasePath || '/iset-wizard';
        const href = r.stepId ? `${basePath}?step=${encodeURIComponent(r.stepId)}` : undefined;
        return (
          <div className="govuk-summary-list__row" key={i}>
            <dt className="govuk-summary-list__key">{r.label}</dt>
            <dd className="govuk-summary-list__value">{r.value}</dd>
            {r.stepId && (
              <dd className="govuk-summary-list__actions">
                <a
                  className="govuk-link"
                  href={href}
                  onClick={onEdit ? (e) => { e.preventDefault(); onEdit(r.stepId); } : undefined}
                >
                  {actionText}<span className="govuk-visually-hidden"> {r.label}</span>
                </a>
              </dd>
            )}
          </div>
        );
      })}
    </dl>
  );
}

export function DateParts({ comp, lang = 'en', value, onChange, error }) {
  useGovUkInit();
  const name = comp.storageKey || comp.id || 'date';
  const id = name;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const required = !!comp.required;
  let v = { day: '', month: '', year: '' };
  if (value && typeof value === 'object') {
    v = { ...v, ...value };
  } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-');
    v = { day: d, month: m, year: y };
  }
  const clsGroup = `govuk-form-group${error ? ' govuk-form-group--error' : ''}`;
  const commit = (next) => {
    if (!onChange) return;
    const n = (comp.normalize || '').toLowerCase();
    if (n === 'date-iso') {
      const d = String(next.day || '').padStart(2, '0');
      const m = String(next.month || '').padStart(2, '0');
      const y = String(next.year || '');
      if (y.length === 4 && m !== '00' && d !== '00') {
        onChange(`${y}-${m}-${d}`);
        return;
      }
    }
    onChange(next);
  };
  const onChangePart = (key) => (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    commit({ ...v, [key]: val });
  };
  return (
    <div className={clsGroup}>
      <fieldset className="govuk-fieldset" aria-describedby={[comp.hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}>
        {comp.label && (
          <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">{t(lang, comp.label)}</legend>
        )}
        {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
        {error && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>}
    <div className="govuk-date-input" id={id} data-module="govuk-date-input">
          <div className="govuk-date-input__item">
            <div className="govuk-form-group">
      <label className="govuk-label govuk-date-input__label" htmlFor={`${id}-day`}>{lang === 'fr' ? 'Jour' : 'Day'}</label>
              <input className={`govuk-input govuk-date-input__input govuk-input--width-2${error ? ' govuk-input--error' : ''}`} id={`${id}-day`} name={`${name}-day`} type="text" inputMode="numeric" pattern="[0-9]*" value={v.day || ''} onChange={onChangePart('day')} required={required} aria-invalid={error ? 'true' : undefined} />
            </div>
          </div>
          <div className="govuk-date-input__item">
            <div className="govuk-form-group">
      <label className="govuk-label govuk-date-input__label" htmlFor={`${id}-month`}>{lang === 'fr' ? 'Mois' : 'Month'}</label>
              <input className={`govuk-input govuk-date-input__input govuk-input--width-2${error ? ' govuk-input--error' : ''}`} id={`${id}-month`} name={`${name}-month`} type="text" inputMode="numeric" pattern="[0-9]*" value={v.month || ''} onChange={onChangePart('month')} required={required} aria-invalid={error ? 'true' : undefined} />
            </div>
          </div>
          <div className="govuk-date-input__item">
            <div className="govuk-form-group">
      <label className="govuk-label govuk-date-input__label" htmlFor={`${id}-year`}>{lang === 'fr' ? 'Année' : 'Year'}</label>
              <input className={`govuk-input govuk-date-input__input govuk-input--width-4${error ? ' govuk-input--error' : ''}`} id={`${id}-year`} name={`${name}-year`} type="text" inputMode="numeric" pattern="[0-9]*" value={v.year || ''} onChange={onChangePart('year')} required={required} aria-invalid={error ? 'true' : undefined} />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
}

export function Input({ comp, lang = 'en', value = '', onChange, error }) {
  useGovUkInit();
  const name = comp.storageKey || comp.id || 'input';
  const id = name;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  // Determine effective input type. Some masks inject formatting characters (spaces, parentheses, dashes, commas)
  // that are invalid for <input type="number">. Using type="number" causes the browser to reject/clear the value
  // when we insert a space after the 3rd digit (e.g., SIN 123 4 -> clears). For any formatting mask we coerce to text
  // while preserving the desired virtual keyboard via inputMode.
  const rawType = comp.inputType || 'text';
  const clsBase = comp.class || 'govuk-input';
  const cls = error ? `${clsBase} govuk-input--error` : clsBase;
  const auto = comp.autocomplete || undefined;
  let inputMode = comp.inputMode || undefined;
  const pattern = comp.pattern || undefined;
  const spellCheck = typeof comp.spellcheck === 'boolean' ? comp.spellcheck : undefined;
  const disabled = !!comp.disabled;
  const required = !!comp.required;
  const mask = (comp.mask || comp.inputMask || '').toLowerCase();
  const maskNeedsTextType = new Set([
    'sin','sin-ca','status-rn','bank-transit-ca','bank-institution-ca',
    'phone-na','ca-phone','phone-ca','canadian-phone','us-phone',
    'postal-code-ca','postal-code-us','currency','date-iso','time-hm'
  ]);
  const type = maskNeedsTextType.has(mask) ? 'text' : rawType;
  // Provide sensible inputMode hints when we overrode the type
  if (maskNeedsTextType.has(mask) && !inputMode) {
    if (['currency'].includes(mask)) inputMode = 'decimal';
    else if (['time-hm'].includes(mask)) inputMode = 'numeric';
    else inputMode = 'numeric'; // digits-focused keyboard
  }
  // Formatting helpers ----------------------------------------------------
  const formatters = {
    sin(raw) {
      const d = (raw || '').replace(/\D+/g, '').slice(0,9);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `${d.slice(0,3)} ${d.slice(3)}`;
      return `${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6)}`;
    },
    phoneNA(raw) {
      const d = (raw || '').replace(/\D+/g, '').slice(0,10);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
      return `(${d.slice(0,3)}) ${d.slice(3,6)} ${d.slice(6)}`;
    },
    statusRN(raw) {
      const d = (raw || '').replace(/\D+/g, '').slice(0,10);
      if (d.length <= 3) return d;
      return `${d.slice(0,3)} ${d.slice(3)}`;
    },
    bankTransitCA(raw) {
      return (raw || '').replace(/\D+/g, '').slice(0,5);
    },
    bankInstitutionCA(raw) {
      return (raw || '').replace(/\D+/g, '').slice(0,3);
    },
    postalCA(raw) {
      const up = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0,6);
      const pattern = ['L','D','L','D','L','D'];
      let out = ''; let j = 0;
      for (let i=0; i<up.length && j<pattern.length; i++) {
        const ch = up[i];
        const want = pattern[j];
        if (want === 'L' && /[A-Z]/.test(ch)) { out += ch; j++; continue; }
        if (want === 'D' && /[0-9]/.test(ch)) { out += ch; j++; continue; }
      }
      if (out.length <= 3) return out;
      return `${out.slice(0,3)} ${out.slice(3)}`;
    },
    postalUS(raw) {
      const d = (raw || '').replace(/\D+/g, '').slice(0,9);
      if (d.length <= 5) return d;
      return `${d.slice(0,5)}-${d.slice(5)}`;
    },
    dateISO(raw) {
      const d = (raw || '').replace(/[^0-9]/g, '').slice(0,8);
      if (d.length <= 4) return d;
      if (d.length <= 6) return `${d.slice(0,4)}-${d.slice(4)}`;
      return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6)}`;
    },
    timeHM(raw) {
      const d = (raw || '').replace(/[^0-9]/g, '').slice(0,4);
      if (d.length <= 2) return d;
      return `${d.slice(0,2)}:${d.slice(2)}`;
    },
    currency(raw) {
      if (!raw) return '';
      let c = String(raw).replace(/[^0-9.]/g, '');
      const dot = c.indexOf('.');
      if (dot >= 0) c = c.slice(0, dot + 1) + c.slice(dot + 1).replace(/\./g, '');
      c = c.replace(/(\.\d{2}).*/, '$1');
      if (c === '.') return '';
      const [intPart, decPart] = c.split('.');
      const intFmt = intPart.replace(/^0+(\d)/, '$1').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return decPart !== undefined ? `${intFmt}.${decPart}` : intFmt;
    },
  };
  const applyMask = (raw) => {
    switch (mask) {
      case 'sin':
      case 'sin-ca': return formatters.sin(raw);
      case 'status-rn': return formatters.statusRN(raw);
      case 'phone-na':
      case 'ca-phone':
      case 'phone-ca':
      case 'canadian-phone':
      case 'us-phone': return formatters.phoneNA(raw);
      case 'bank-transit-ca': return formatters.bankTransitCA(raw);
      case 'bank-institution-ca': return formatters.bankInstitutionCA(raw);
      case 'postal-code-ca': return formatters.postalCA(raw);
      case 'postal-code-us': return formatters.postalUS(raw);
      case 'date-iso': return formatters.dateISO(raw);
      case 'time-hm': return formatters.timeHM(raw);
      case 'currency': return formatters.currency(raw);
      default: return raw;
    }
  };
  const handleChange = (e) => {
    if (!onChange) return;
    const formatted = applyMask(e.target.value);
    onChange(formatted);
  };
  const handleBlur = (e) => {
    if (typeof comp.normalize === 'string' && comp.normalize.toLowerCase() === 'trim') {
      const trimmed = (e.target.value || '').trim();
      if (trimmed !== e.target.value) onChange && onChange(trimmed);
    }
  };
  useEffect(() => {
    if (!onChange) return;
    if (!value) return;
    const formatted = applyMask(value);
    if (formatted !== value) onChange(formatted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const describedBy = [comp.hint ? hintId : null, error ? errorId : null, comp.extraDescribedBy ? comp.extraDescribedBy : null].filter(Boolean).join(' ') || undefined;
  const groupCls = `govuk-form-group${error ? ' govuk-form-group--error' : ''}${comp.formGroupClass ? ' ' + comp.formGroupClass : ''}`;
  return (
    <div className={groupCls}>
      {comp.label && (
        <label className={`govuk-label${comp.labelClass ? ' ' + comp.labelClass : ''}`} htmlFor={id}>{t(lang, comp.label)}</label>
      )}
      {comp.hint && <div id={hintId} className="govuk-hint">{t(lang, comp.hint)}</div>}
      {error && <p id={errorId} className="govuk-error-message"><span className="govuk-visually-hidden">Error:</span> {error}</p>}
      <div className="govuk-input__wrapper">
        {comp.prefix && (
          <div className={`govuk-input__prefix${comp.prefix.classes ? ' ' + comp.prefix.classes : ''}`}>{t(lang, comp.prefix.text)}</div>
        )}
        <input
          className={cls}
          id={id}
          name={name}
          type={type}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-describedby={describedBy}
          autoComplete={auto}
          inputMode={inputMode}
          pattern={pattern}
          min={comp.min || undefined}
          max={comp.max || undefined}
          spellCheck={spellCheck}
          required={required}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
        />
        {comp.suffix && (
          <div className={`govuk-input__suffix${comp.suffix.classes ? ' ' + comp.suffix.classes : ''}`}>{t(lang, comp.suffix.text)}</div>
        )}
      </div>
    </div>
  );
}

export const registry = {
  radio: Radio,
  panel: Panel,
  input: Input,
  // Aliases mapping families to primitives
  text: Input,
  email: Input,
  phone: Input,
  password: Input,
  number: Input,
  textarea: Textarea,
  select: Select,
  checkbox: Checkbox,
  date: DateParts,
  label: Label,
  paragraph: Paragraph,
  'inset-text': InsetText,
  'warning-text': WarningText,
  details: Details,
  accordion: Accordion,
  'character-count': CharacterCount,
  'file-upload': FileUpload,
  'date-input': DateParts,
  checkboxes: Checkbox,
  'password-input': Input,
  'summary-list': SummaryList,
  'signature-ack': SignatureAck,
};

